import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GitBranch, Edit, Trash2, Play, ListOrdered, Zap } from 'lucide-react';

export const FlowCard = ({ flow, onEdit, onDelete, onTest }) => {
  const { t } = useTranslation('workflows');
  const isActive = flow.status === 'active';
  
  return (
    <Card className={`group hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border-[var(--line)] bg-[var(--surface)] ${!isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 'var(--r-lg)' }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${isActive ? 'bg-[var(--red-soft)]' : 'bg-[var(--bg-alt)]'}`}>
              <GitBranch className={`h-5 w-5 ${isActive ? 'text-[var(--red)]' : 'text-[var(--ink-3)]'}`} />
            </div>
            <div>
              <p className="font-semibold text-[var(--ink)] text-[15px]">{flow.name}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant={isActive ? 'default' : 'secondary'} className="text-[10px] font-semibold">
                  {isActive ? t('flows.active', 'Active') : t('flows.inactive', 'Inactive')}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-medium">
                  P: {flow.priority}
                </Badge>
                {flow.is_fallback && (
                  <Badge variant="destructive" className="text-[10px]">{t('flows.fallback', 'Fallback')}</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {flow.description && (
          <p className="text-[13px] text-[var(--ink-2)] mb-4 line-clamp-2">{flow.description}</p>
        )}
        
        <div className="flex items-center gap-4 text-[12px] text-[var(--ink-3)] mb-4">
          <span className="flex items-center gap-1.5">
            <ListOrdered className="h-4 w-4" />
            {flow.task_count || 0} {t('flows.tasks', 'tasks')}
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="h-4 w-4" />
            {(flow.trigger_keywords?.length || 0) + (flow.trigger_phrases?.length || 0)} triggers
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-[var(--line-soft)]">
          <Button variant="ghost" size="sm" onClick={() => onTest(flow)} className="h-8 text-[12px] font-medium text-[var(--ink-2)] hover:text-[var(--red)]">
            <Play className="h-4 w-4 mr-1" />
            {t('flows.test', 'Test')}
          </Button>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={() => onEdit(flow)} className="h-8 w-8 p-0">
              <Edit className="h-4 w-4 text-[var(--ink-3)]" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(flow.id)} className="h-8 w-8 p-0">
              <Trash2 className="h-4 w-4 text-[var(--red)]" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FlowCard;
